import { useState } from 'react'
import { useMutation } from '@apollo/client'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Alert,
  AlertTitle,
  FormControlLabel,
  Checkbox,
} from '@mui/material'

import { NotificationDialog } from '.'
import { LoginForm } from './login/login-form'
import { closeMyAccount } from '../graphql/mutations/user'

export function DeleteAccountModal({ open, onClose, dispatchStep }) {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [loginError, setLoginError] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)

  const [closeAccount, { loading: deleteLoading }] = useMutation(closeMyAccount, {
    onCompleted: () => {
      setShowSuccess(true)
      setIsLoading(false)
    },
    onError: (errorDelete) => {
      setError(`Failed to close account: ${errorDelete.message}`)
      setIsLoading(false)
    },
  })

  const handleLoginSuccess = () => {
    setLoginError('')
    setShowConfirmation(true)
  }

  const handleLoginError = (errorMessage) => {
    setLoginError(errorMessage || 'Login failed. Please check your credentials.')
  }

  const handleDeleteConfirm = async () => {
    setIsLoading(true)
    setError('')

    try {
      await closeAccount()
    } catch (err) {
      console.error('Close account error:', err)
    }
  }

  const handleClose = () => {
    setShowConfirmation(false)
    setShowSuccess(false)
    setError('')
    setLoginError('')
    setIsLoading(false)
    setAcknowledged(false)
    onClose()
  }

  if (showSuccess) {
    return (
      <NotificationDialog
        open={open}
        onClose={() => {
          window.location.href = '/logout'
        }}
        type="success"
        title="Account Deleted"
        message="Your account has been deleted immediately. You will now be logged out."
        actions={[
          {
            label: 'OK',
            onClick: () => {
              window.location.href = '/logout'
            },
          },
        ]}
      />
    )
  }

  if (showConfirmation) {
    return (
      <NotificationDialog
        open={open}
        onClose={handleClose}
        type="alert"
        title="Delete Account"
        message={
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Deleting your account will permanently remove your access to:</AlertTitle>
              <ul>
                <li>Your Giving Fund</li>
                <li>All donation history</li>
                <li>Access to tax receipts</li>
              </ul>
            </Alert>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to delete your GiveWise Giving Fund account? This will immediately delete your
              account and all associated portals and funds.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} color="primary" />
              }
              label="I understand that deleting my account will take effect immediately"
              sx={{ mb: 2 }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        }
        actions={[
          { label: 'Cancel', onClick: handleClose },
          {
            label: isLoading || deleteLoading ? 'Deleting...' : 'Delete Account',
            onClick: handleDeleteConfirm,
            disabled: isLoading || deleteLoading || !acknowledged,
          },
        ]}
      />
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Login to Delete Account</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please log in to proceed with deleting your account.
        </Typography>

        {loginError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loginError}
          </Alert>
        )}

        <LoginForm onCompleted={handleLoginSuccess} onError={handleLoginError} hideSignUpLink />
      </DialogContent>
    </Dialog>
  )
}
